import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Box,
  Autocomplete,
  CircularProgress,
  Avatar,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import api, { API_BASE_URL } from "../../utils/api";

// U.S. States with FIPS Codes for Census API
const usStates = [
  { name: "Alabama", code: "01" },
  { name: "Alaska", code: "02" },
  { name: "Arizona", code: "04" },
  { name: "Arkansas", code: "05" },
  { name: "California", code: "06" },
  { name: "Colorado", code: "08" },
  { name: "Connecticut", code: "09" },
  { name: "Delaware", code: "10" },
  { name: "Florida", code: "12" },
  { name: "Georgia", code: "13" },
  { name: "Hawaii", code: "15" },
  { name: "Idaho", code: "16" },
  { name: "Illinois", code: "17" },
  { name: "Indiana", code: "18" },
  { name: "Iowa", code: "19" },
  { name: "Kansas", code: "20" },
  { name: "Kentucky", code: "21" },
  { name: "Louisiana", code: "22" },
  { name: "Maine", code: "23" },
  { name: "Maryland", code: "24" },
  { name: "Massachusetts", code: "25" },
  { name: "Michigan", code: "26" },
  { name: "Minnesota", code: "27" },
  { name: "Mississippi", code: "28" },
  { name: "Missouri", code: "29" },
  { name: "Montana", code: "30" },
  { name: "Nebraska", code: "31" },
  { name: "Nevada", code: "32" },
  { name: "New Hampshire", code: "33" },
  { name: "New Jersey", code: "34" },
  { name: "New Mexico", code: "35" },
  { name: "New York", code: "36" },
  { name: "North Carolina", code: "37" },
  { name: "North Dakota", code: "38" },
  { name: "Ohio", code: "39" },
  { name: "Oklahoma", code: "40" },
  { name: "Oregon", code: "41" },
  { name: "Pennsylvania", code: "42" },
  { name: "Rhode Island", code: "44" },
  { name: "South Carolina", code: "45" },
  { name: "South Dakota", code: "46" },
  { name: "Tennessee", code: "47" },
  { name: "Texas", code: "48" },
  { name: "Utah", code: "49" },
  { name: "Vermont", code: "50" },
  { name: "Virginia", code: "51" },
  { name: "Washington", code: "53" },
  { name: "West Virginia", code: "54" },
  { name: "Wisconsin", code: "55" },
  { name: "Wyoming", code: "56" },
];

const emptyForm = {
  companyID: "",
  name: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  phoneNumber: "",
  email: "",
  website: "",
  createdAt: "",
  updatedAt: "",
};

const normalizeCityName = (name = "") => {
  return String(name)
    .replace(/\s+(city|town|village|CDP|municipality|borough)$/i, "")
    .replace(/\s*\(balance\)$/i, "")
    .trim();
};

const getStateByCode = (code) => usStates.find((state) => state.code === code);

const getStateByNameOrCode = (value) =>
  usStates.find(
    (state) =>
      state.code === value ||
      state.name.toLowerCase() === String(value || "").toLowerCase()
  );

const Company = () => {
  const [companies, setCompanies] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityCache, setCityCache] = useState({});
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [formData, setFormData] = useState(emptyForm);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [existingLogo, setExistingLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const loadCompanies = async () => {
    try {
      const res = await api.get(`/companies`);
      setCompanies(res.data || []);
    } catch (err) {
      console.error("Error loading companies:", err);
    }
  };

  const resetLogoState = () => {
    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview("");
    setExistingLogo(null);
  };

  const loadCompanyLogo = async (companyId) => {
    if (!companyId) {
      resetLogoState();
      return;
    }

    try {
      setLogoLoading(true);
      const res = await api.get(`/company-logos/${companyId}`);
      const logo = res.data || null;

      setExistingLogo(logo);

      if (logo?.file_url) {
        setLogoPreview(`${API_BASE_URL}${logo.file_url}`);
      } else {
        setLogoPreview("");
      }

      setLogoFile(null);
    } catch (error) {
      setExistingLogo(null);
      setLogoFile(null);
      setLogoPreview("");
      if (error?.response?.status !== 404) {
        console.error("Error loading company logo:", error);
      }
    } finally {
      setLogoLoading(false);
    }
  };

  const fetchCities = async (stateFips) => {
    if (!stateFips) {
      setCities([]);
      return;
    }

    if (cityCache[stateFips]) {
      setCities(cityCache[stateFips]);
      return;
    }

    try {
      setCitiesLoading(true);

      const response = await axios.get(
        `https://api.census.gov/data/2019/pep/population?get=NAME&for=place:*&in=state:${stateFips}`
      );

      if (Array.isArray(response.data) && response.data.length > 1) {
        const cleanedCities = [
          ...new Set(
            response.data
              .slice(1)
              .map((item) => normalizeCityName(item[0]))
              .filter(Boolean)
          ),
        ].sort((a, b) => a.localeCompare(b));

        setCities(cleanedCities);
        setCityCache((prev) => ({
          ...prev,
          [stateFips]: cleanedCities,
        }));
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleCompanySelect = async (companyId) => {
    setSelectedCompany(companyId);

    if (!companyId) {
      setFormData(emptyForm);
      setCities([]);
      resetLogoState();
      return;
    }

    try {
      const response = await api.get(`/companies/${companyId}`);
      const company = response.data;

      const matchedState = getStateByNameOrCode(company.State || "");
      const stateCode = matchedState?.code || "";

      const nextForm = {
        companyID: company.CompanyID || "",
        name: company.Name || "",
        address: company.Address || "",
        city: company.City || "",
        state: stateCode,
        zipCode: company.ZipCode || "",
        phoneNumber: company.PhoneNumber || "",
        email: company.Email || "",
        website: company.Website || "",
        createdAt: company.CreatedAt || "",
        updatedAt: company.UpdatedAt || "",
      };

      setFormData(nextForm);

      if (stateCode) {
        await fetchCities(stateCode);
      } else {
        setCities([]);
      }

      await loadCompanyLogo(companyId);
    } catch (error) {
      console.error("Error fetching company details:", error);
      setSnackbar({
        open: true,
        message: "Failed to load company details.",
        severity: "error",
      });
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: "Please select a valid image file (PNG, JPG, JPEG, WEBP, SVG).",
        severity: "error",
      });
      return;
    }

    if (logoPreview && logoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadOrUpdateLogo = async (companyId) => {
    if (!companyId || !logoFile) return;

    const formDataToSend = new FormData();
    formDataToSend.append("logo", logoFile);
    formDataToSend.append("company_id", companyId);

    try {
      setLogoSaving(true);

      if (existingLogo) {
        await api.put(`/company-logos/${companyId}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post(`/company-logos`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await loadCompanyLogo(companyId);
    } catch (error) {
      console.error("Error uploading/updating logo:", error);
      throw error;
    } finally {
      setLogoSaving(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!selectedCompany) {
      setSnackbar({
        open: true,
        message: "Please select a company first.",
        severity: "warning",
      });
      return;
    }

    try {
      setLogoSaving(true);
      await api.delete(`/company-logos/${selectedCompany}`);
      resetLogoState();

      setSnackbar({
        open: true,
        message: "Company logo deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting logo:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete company logo.",
        severity: "error",
      });
    } finally {
      setLogoSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || formData.name.trim() === "") {
      setSnackbar({
        open: true,
        message: "Company name is required!",
        severity: "error",
      });
      return;
    }

    const selectedState = getStateByCode(formData.state);
    const stateName = selectedState?.name || null;

    if (!stateName) {
      setSnackbar({
        open: true,
        message: "Invalid state selection!",
        severity: "error",
      });
      return;
    }

    const payload = {
      Name: formData.name.trim(),
      Address: formData.address?.trim() || null,
      City: formData.city?.trim() || null,
      State: stateName,
      ZipCode: formData.zipCode?.trim() || null,
      PhoneNumber: formData.phoneNumber?.trim() || null,
      Email: formData.email?.trim() || null,
      Website: formData.website?.trim() || null,
    };

    try {
      let companyId = selectedCompany;

      if (selectedCompany) {
        await api.put(`/companies/${parseInt(selectedCompany, 10)}`, payload);
      } else {
        const createRes = await api.post(`/companies`, payload);
        companyId =
          createRes?.data?.CompanyID ||
          createRes?.data?.companyID ||
          createRes?.data?.id ||
          createRes?.data?.insertId ||
          "";
      }

      if (logoFile && companyId) {
        await uploadOrUpdateLogo(companyId);
      }

      setSnackbar({
        open: true,
        message:
          selectedCompany
            ? "Company updated successfully! ✅"
            : "Company created successfully! 🎉",
        severity: "success",
      });

      await loadCompanies();

      if (!selectedCompany && companyId) {
        setSelectedCompany(companyId);
        await handleCompanySelect(companyId);
      } else if (selectedCompany) {
        await loadCompanyLogo(selectedCompany);
      }
    } catch (error) {
      console.error("Error saving company:", error);
      console.error("API response:", error.response?.data);

      setSnackbar({
        open: true,
        message: "Failed to save company or logo!",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/companies/${selectedCompany}`);

      setSnackbar({
        open: true,
        message: "Company deleted!",
        severity: "success",
      });

      setSelectedCompany("");
      setFormData(emptyForm);
      setCities([]);
      resetLogoState();

      const updatedCompanies = await axios.get(`${API_BASE_URL}/companies`);
      setCompanies(updatedCompanies.data || []);
    } catch (error) {
      console.error("Error deleting company:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete company!",
        severity: "error",
      });
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#fff",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 760,
        p: { xs: 2, sm: 3, md: 4 },
        mx: "auto",
        mt: 1,
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: "#223142",
          mb: 0.5,
        }}
      >
        Manage Companies
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#6b778c",
          mb: 3,
        }}
      >
        Create, edit, and manage company information.
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Company</InputLabel>
        <Select
          value={selectedCompany}
          label="Company"
          onChange={(e) => handleCompanySelect(e.target.value)}
          sx={{ borderRadius: "12px", backgroundColor: "#fff" }}
        >
          <MenuItem value="">Add New</MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.CompanyID} value={c.CompanyID}>
              {c.Name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid rgba(47,99,173,0.10)",
          borderRadius: "16px",
          background: "#f8fbff",
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1.5, color: "#223142" }}>
          Company Logo
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Avatar
            src={logoPreview || ""}
            alt="Company Logo"
            variant="rounded"
            sx={{
              width: 96,
              height: 96,
              borderRadius: "16px",
              bgcolor: "#eef4fb",
              border: "1px solid rgba(47,99,173,0.12)",
            }}
          >
            {formData.name?.charAt(0)?.toUpperCase() || "C"}
          </Avatar>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Choose Logo
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleLogoFileChange}
              />
            </Button>

            {(existingLogo || logoFile) && (
              <Button
                color="error"
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteLogo}
                disabled={!selectedCompany || logoSaving}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Remove Logo
              </Button>
            )}

            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              PNG, JPG, JPEG, WEBP, or SVG
            </Typography>

            {logoFile && (
              <Typography variant="caption" sx={{ color: "#2f63ad", fontWeight: 600 }}>
                Selected: {logoFile.name}
              </Typography>
            )}

            {logoLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading logo...</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TextField
          label="Company Name"
          name="name"
          fullWidth
          value={formData.name}
          onChange={handleTextChange}
          sx={fieldSx}
        />

        <TextField
          label="Phone Number"
          name="phoneNumber"
          fullWidth
          value={formData.phoneNumber}
          onChange={handleTextChange}
          sx={fieldSx}
        />

        <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
          <TextField
            label="Address"
            name="address"
            fullWidth
            value={formData.address}
            onChange={handleTextChange}
            sx={fieldSx}
          />
        </Box>

        <Autocomplete
          options={usStates}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={usStates.find((s) => s.code === formData.state) || null}
          onChange={async (_, newValue) => {
            const stateCode = newValue?.code || "";

            setFormData((prev) => ({
              ...prev,
              state: stateCode,
              city: "",
            }));

            if (stateCode) {
              await fetchCities(stateCode);
            } else {
              setCities([]);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="State" fullWidth sx={fieldSx} />
          )}
        />

        <Autocomplete
          freeSolo
          options={cities}
          value={formData.city || ""}
          inputValue={formData.city || ""}
          onInputChange={(_, newInputValue) => {
            handleFieldChange("city", newInputValue || "");
          }}
          onChange={(_, newValue) => {
            handleFieldChange("city", newValue || "");
          }}
          loading={citiesLoading}
          disabled={!formData.state}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City"
              fullWidth
              sx={fieldSx}
              helperText={!formData.state ? "Select a state first" : ""}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {citiesLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          label="Zip Code"
          name="zipCode"
          fullWidth
          value={formData.zipCode}
          onChange={handleTextChange}
          sx={fieldSx}
        />

        <TextField
          label="Email"
          name="email"
          fullWidth
          value={formData.email}
          onChange={handleTextChange}
          sx={fieldSx}
        />

        <Box sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}>
          <TextField
            label="Website"
            name="website"
            fullWidth
            value={formData.website}
            onChange={handleTextChange}
            sx={fieldSx}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          gap: 1.5,
          mt: 3,
        }}
      >
        {selectedCompany && (
          <Button
            color="error"
            variant="outlined"
            onClick={handleDelete}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Delete
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={logoSaving}
          sx={{
            backgroundColor: "#2f63ad",
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            "&:hover": {
              backgroundColor: "#1f4f94",
            },
          }}
        >
          {logoSaving ? "Saving..." : "Save"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default Company;